<script setup lang="ts">
/**
 * Ops App Root Component
 */
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore, useUIStore, MermaidZoom, ToastBridge } from '@claimshield/shared'
import ChatPanel from './components/ChatPanel.vue'
import FloatingActionBar from './components/FloatingActionBar.vue'
import SupportRequestDialog from './components/SupportRequestDialog.vue'
import ReloadPrompt from './components/ReloadPrompt.vue'
import RobleyShortcuts from './views/robley/_RobleyShortcuts.vue'
import CommandPalette from './components/command-palette/CommandPalette.vue'
import RouteDenialNotice from './components/RouteDenialNotice.vue'
import { useSupportStore } from './stores/supportStore'
import { capturePageContext } from './lib/pageContext'

const authStore = useAuthStore()
const uiStore = useUIStore()
const support = useSupportStore()
const route = useRoute()
const chatOpen = ref(false)
const notesOpen = ref(false)

function toggleChat() {
  chatOpen.value = !chatOpen.value
}

function handleToggleNotes() {
  // Ask DashboardView's ClientNotesWidget to slide its drawer out. `notesOpen`
  // is NOT set here: the bar hides on the widget's `notes-panel-opened` ack, so
  // on routes where the widget is not mounted nothing opens and nothing hides.
  window.dispatchEvent(new CustomEvent('open-notes-panel'))
}

function handleToggleRequest() {
  // If an error nudge is pending, the megaphone IS the report button —
  // prefill the dialog with the triggering error (single entry point).
  const triggeringError = support.nudge
  if (triggeringError) support.dismissNudge()
  support.openRequest({
    request_type: 'bug',
    description: triggeringError ? `Error: ${triggeringError.message}` : '',
    page_context: capturePageContext(route, { recentErrors: support.errorLog, triggeringError: triggeringError ?? undefined }),
  })
}

onMounted(async () => {
  // The FAB's notes-open state mirrors the drawer itself — opened by its ack,
  // cleared when it closes from its own header OR unmounts on a route change.
  window.addEventListener('notes-panel-opened', () => { notesOpen.value = true })
  window.addEventListener('notes-panel-closed', () => { notesOpen.value = false })
  // Initialize UI (theme, sidebar state)
  uiStore.initialize()
  // Initialize auth
  await authStore.initialize()
})
</script>

<template>
  <PToast position="top-right" :baseZIndex="9999" />
  <ToastBridge />
  <MermaidZoom />
  <ReloadPrompt />
  <RouterView />
  <!-- Says why a role bounce landed here (V-FE L2); the guard records it. -->
  <RouteDenialNotice />
  <template v-if="authStore.isAuthenticated">
    <ChatPanel v-model:open="chatOpen" />
    <FloatingActionBar :panel-open="chatOpen || notesOpen" @toggle-chat="toggleChat" @toggle-notes="handleToggleNotes" @toggle-request="handleToggleRequest" />
    <SupportRequestDialog />
    <!-- Keyboard shortcuts for /robley/* routes — self-guards, no-op elsewhere -->
    <RobleyShortcuts />
    <!-- ⌘K command palette — jump to pages, records, actions -->
    <CommandPalette />
  </template>
</template>
